/**
 * useFinanceSearch — Debounced search hook for the finance search API.
 *
 * Calls GET /api/v1/search with 300ms debounce.
 * Returns results grouped by customers, vouchers, ledgers.
 * Only fires when query >= 2 characters.
 */

import { useState, useEffect, useRef } from 'react'
import { financeSearch } from '@/services/finance/financeService'
import type { SearchResultsResponse } from '../types'

const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 2

interface UseFinanceSearchResult {
  results: SearchResultsResponse | null
  isSearching: boolean
  error: string | null
  hasResults: boolean
}

export function useFinanceSearch(query: string): UseFinanceSearchResult {
  const [results, setResults] = useState<SearchResultsResponse | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Clear previous timer
    if (timerRef.current) clearTimeout(timerRef.current)

    // Reset if query too short
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      setResults(null)
      setIsSearching(false)
      setError(null)
      return
    }

    setIsSearching(true)

    timerRef.current = setTimeout(async () => {
      // Cancel previous request
      if (abortRef.current) abortRef.current.abort()
      abortRef.current = new AbortController()

      try {
        const data = await financeSearch(query.trim(), undefined, 5)
        setResults(data)
        setError(null)
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return
        setError(e instanceof Error ? e.message : 'Search failed')
      } finally {
        setIsSearching(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [query])

  const hasResults =
    results !== null &&
    (results.customers.length > 0 ||
      results.vouchers.length > 0 ||
      results.ledgers.length > 0)

  return { results, isSearching, error, hasResults }
}
